import { uploadApi } from '../../../apis/uploadApi';
import Vue from 'vue';
import {
  MAX_CONCURRENT_UPLOADS,
  PROGRESS_COMMIT_MIN_INTERVAL_MS,
  PROGRESS_COMMIT_MIN_STEP
} from './uploadLimits';

export {
  ZIP_BROWSER_VALIDATE_MAX_BYTES,
  ZIP_UPLOAD_MAX_BYTES
} from './uploadLimits';

export const LoadStatus = {
  initial: 'initial',
  loading: 'loading',
  loaded: 'loaded',
  retry: 'retry',
  error: 'error'
};

export const ResourceTypes = {
  RGB: 'RGB',
  IR: 'IR',
  CSV: 'CSV',
  JSON: 'JSON'
};

const state = {
  uploads: {}
};

const getters = {
  uploadProgress: (state) => (uploadId, resourceType) =>
    state.uploads[uploadId]?.uploadProgress[resourceType] || {},
  progressPerUpload: (state) => (uploadId) =>
    state.uploads[uploadId]?.progressPerUpload || {},
  activeUploadIds: (state) =>
    Object.entries(state.uploads)
      .filter(([, uploadData]) => Object.keys(uploadData).length > 0)
      .map(([uploadId]) => uploadId) || {},
  uploads: (state) => (uploadId) => state.uploads[uploadId] || {}
};

const MAX_RETRY = 2;

/** Vue 반응형 밖 — 대량 File[] 큐 (OOM·관찰 오버헤드 방지) */
const uploadQueues = new Map();

function queueKey(uploadId, resourceType) {
  return `${uploadId}:${resourceType}`;
}

function getUploadQueue(uploadId, resourceType) {
  return uploadQueues.get(queueKey(uploadId, resourceType)) || [];
}

function setUploadQueue(uploadId, resourceType, files) {
  uploadQueues.set(queueKey(uploadId, resourceType), files);
}

function clearAllQueuesForUpload(uploadId) {
  const prefix = `${uploadId}:`;
  for (const key of [...uploadQueues.keys()]) {
    if (key.startsWith(prefix)) {
      uploadQueues.delete(key);
    }
  }
}

/** uploadId별 파일 진행률 추적 (Vue 반응형 제외) */
const uploadProgressTrackers = new WeakMap();

const progressCommitThrottle = new Map();

function trackerKey(uploadId, fileKey) {
  return `${uploadId}:${fileKey}`;
}

function getFileTracker(upload) {
  if (!uploadProgressTrackers.has(upload)) {
    uploadProgressTrackers.set(upload, Object.create(null));
  }
  return uploadProgressTrackers.get(upload);
}

function shouldCommitProgress(uploadId, fileKey, progress) {
  if (progress >= 100) {
    return true;
  }
  const key = trackerKey(uploadId, fileKey);
  const now = Date.now();
  const prev = progressCommitThrottle.get(key);
  if (!prev) {
    progressCommitThrottle.set(key, { progress, time: now });
    return true;
  }
  if (
    progress - prev.progress >= PROGRESS_COMMIT_MIN_STEP ||
    now - prev.time >= PROGRESS_COMMIT_MIN_INTERVAL_MS
  ) {
    progressCommitThrottle.set(key, { progress, time: now });
    return true;
  }
  return false;
}

function clearProgressThrottle(uploadId, fileKey) {
  progressCommitThrottle.delete(trackerKey(uploadId, fileKey));
}

function clearAllProgressThrottle(uploadId) {
  const prefix = `${uploadId}:`;
  for (const key of [...progressCommitThrottle.keys()]) {
    if (key.startsWith(prefix)) {
      progressCommitThrottle.delete(key);
    }
  }
}

function defaultProgressPerUpload() {
  return {
    totalProgress: 0,
    totalErrors: 0,
    totalFileSize: 0,
    uploadedFileSize: 0,
    completedCount: 0,
    totalFileCount: 0
  };
}

function defaultUploadShell(reportId, reportName, resourceType) {
  return {
    reportId: reportId ?? '',
    reportName: reportName ?? '',
    resourceType,
    uploadProgress: {},
    progressPerUpload: defaultProgressPerUpload(),
    loadStatus: LoadStatus.initial,
    files: []
  };
}

const mutations = {
  SET_UPLOAD_PROGRESS(
    state,
    { uploadId, resourceType, fileKey, progress, fileSize }
  ) {
    const upload = state.uploads[uploadId];
    if (!upload) {
      return;
    }

    if (!upload.uploadProgress[resourceType]) {
      Vue.set(upload.uploadProgress, resourceType, {});
    }

    if (!upload._completedFileKeys) {
      upload._completedFileKeys = new Set();
    }
    if (progress >= 100 && upload._completedFileKeys.has(fileKey)) {
      return;
    }

    const trackers = getFileTracker(upload);
    const prev = trackers[fileKey] || { progress: 0, size: fileSize || 0 };
    const size = fileSize || prev.size || 0;
    const deltaProgress = Math.max(0, progress - prev.progress);
    const deltaBytes = (deltaProgress / 100) * size;

    if (deltaBytes > 0) {
      const uploaded =
        (upload.progressPerUpload.uploadedFileSize || 0) + deltaBytes;
      Vue.set(upload.progressPerUpload, 'uploadedFileSize', uploaded);
    }

    trackers[fileKey] = { progress, size };

    const { uploadedFileSize, totalFileSize } = upload.progressPerUpload;
    const totalProgress =
      totalFileSize > 0 ? (uploadedFileSize / totalFileSize) * 100 : 0;
    Vue.set(upload.progressPerUpload, 'totalProgress', totalProgress);

    if (progress >= 100) {
      upload._completedFileKeys.add(fileKey);
      delete trackers[fileKey];
      clearProgressThrottle(uploadId, fileKey);
      Vue.delete(upload.uploadProgress[resourceType], fileKey);
      const completed = (upload.progressPerUpload.completedCount || 0) + 1;
      Vue.set(upload.progressPerUpload, 'completedCount', completed);
      return;
    }

    Vue.set(upload.uploadProgress[resourceType], fileKey, {
      progress,
      size,
      error: null
    });
  },
  ADD_UPLOAD_ERROR(state, { uploadId, resourceType, error, fileKey, fileSize }) {
    const upload = state.uploads[uploadId];
    if (!upload) {
      return;
    }
    if (!upload.uploadProgress[resourceType]) {
      Vue.set(upload.uploadProgress, resourceType, {});
    }

    const trackers = getFileTracker(upload);
    delete trackers[fileKey];
    clearProgressThrottle(uploadId, fileKey);
    Vue.delete(upload.uploadProgress[resourceType], fileKey);

    Vue.set(upload.uploadProgress[resourceType], fileKey, {
      progress: 0,
      size: fileSize || 0,
      error
    });
    Vue.set(
      upload.progressPerUpload,
      'totalErrors',
      (upload.progressPerUpload.totalErrors || 0) + 1
    );
  },
  CLEAR_UPLOAD_ERRORS(state, { uploadId }) {
    Vue.set(state.uploads[uploadId].progressPerUpload, 'totalErrors', 0);
  },
  RESET_ALL_UPLOAD(state) {
    state.uploads = {};
    uploadQueues.clear();
  },
  RESET_UPLOAD(state, { uploadId }) {
    clearAllProgressThrottle(uploadId);
    clearAllQueuesForUpload(uploadId);
    Vue.delete(state.uploads, uploadId);
  },
  SET_LOAD_STATUS(state, { uploadId, status }) {
    if (!state.uploads[uploadId]) {
      return;
    }
    Vue.set(state.uploads[uploadId], 'loadStatus', status);
  },
  SET_UPLOAD_QUEUE(state, { reportId, reportName, uploadId, resourceType, files }) {
    if (!state.uploads[uploadId]) {
      Vue.set(
        state.uploads,
        uploadId,
        defaultUploadShell(reportId, reportName, resourceType)
      );
    }

    const queue = getUploadQueue(uploadId, resourceType);
    const existFileKeys = new Set(
      queue.map((file) => `${file.name}_${file.lastModified}`)
    );
    let addedCount = 0;
    files.forEach((file) => {
      const fileKey = `${file.name}_${file.lastModified}`;
      if (!existFileKeys.has(fileKey)) {
        queue.push(file);
        existFileKeys.add(fileKey);
        addedCount += 1;
      }
    });
    setUploadQueue(uploadId, resourceType, queue);

    if (addedCount > 0) {
      const upload = state.uploads[uploadId];
      Vue.set(
        upload.progressPerUpload,
        'totalFileCount',
        (upload.progressPerUpload.totalFileCount || 0) + addedCount
      );
    }
  },
  SLICE_UPLOAD_QUEUE(_state, { uploadId, resourceType, count }) {
    const queue = getUploadQueue(uploadId, resourceType);
    if (!queue.length) {
      return;
    }
    setUploadQueue(uploadId, resourceType, queue.slice(count));
  },
  SET_UPLOAD_ID(state, { reportId, reportName, uploadId, resourceType }) {
    clearAllProgressThrottle(uploadId);
    clearAllQueuesForUpload(uploadId);
    Vue.set(
      state.uploads,
      uploadId,
      defaultUploadShell(reportId, reportName, resourceType)
    );
  },
  ADD_TOTAL_FILE_SIZE(state, { uploadId, addedBytes }) {
    const upload = state.uploads[uploadId];
    if (!upload) {
      return;
    }
    const current = Number(upload.progressPerUpload.totalFileSize) || 0;
    const plus = Number(addedBytes) || 0;
    Vue.set(upload.progressPerUpload, 'totalFileSize', current + plus);
  }
};

function findStaleUploadIds(state, reportId, resourceType) {
  const reportKey = String(reportId);
  return Object.entries(state.uploads)
    .filter(([, upload]) => {
      if (!upload || String(upload.reportId) !== reportKey) {
        return false;
      }
      if (upload.resourceType !== resourceType) {
        return false;
      }
      return upload.loadStatus === LoadStatus.loading
        || upload.loadStatus === LoadStatus.retry
        || upload.loadStatus === LoadStatus.error;
    })
    .map(([uploadId]) => uploadId);
}

function commitProgressIfNeeded(
  commit,
  { uploadId, resourceType, fileKey, progress, fileSize }
) {
  if (!shouldCommitProgress(uploadId, fileKey, progress)) {
    return;
  }
  commit('SET_UPLOAD_PROGRESS', {
    uploadId,
    resourceType,
    fileKey,
    progress,
    fileSize
  });
}

const actions = {
  async uploadFiles(
    { dispatch, commit, state },
    { uploadId, reportId, reportName, resourceType, selectedFiles }
  ) {
    const staleIds = findStaleUploadIds(state, reportId, resourceType);
    staleIds.forEach((staleId) => {
      dispatch('resetUpload', staleId);
    });

    commit('SET_UPLOAD_ID', { reportId, reportName, uploadId, resourceType });
    const files = selectedFiles;

    commit('SET_UPLOAD_QUEUE', {
      reportId,
      reportName,
      uploadId,
      resourceType,
      files
    });

    const batchBytes = files.reduce((acc, file) => acc + file.size, 0);
    commit('ADD_TOTAL_FILE_SIZE', { uploadId, addedBytes: batchBytes });

    if (state.uploads[uploadId].loadStatus !== LoadStatus.loading) {
      commit('SET_LOAD_STATUS', { uploadId, status: LoadStatus.loading });
      await dispatch('processUploadQueue', {
        reportId,
        uploadId,
        resourceType,
        reportName
      });
    }
  },
  async processUploadQueue(
    { dispatch },
    { reportId, uploadId, resourceType, reportName }
  ) {
    const CHUNK_SIZE = 1;
    let activeUploads = 0;

    const startNextChunk = async () => {
      const uploadPromises = [];
      while (
        getUploadQueue(uploadId, resourceType).length > 0 &&
        activeUploads < MAX_CONCURRENT_UPLOADS
      ) {
        const chunk = await dispatch('getChunkFromQueue', {
          uploadId,
          resourceType,
          count: CHUNK_SIZE
        });
        if (!chunk?.length) {
          break;
        }
        activeUploads += 1;
        const uploadPromise = (async () => {
          try {
            await dispatch('uploadChunk', {
              chunk,
              resource: resourceType,
              reportId,
              uploadId,
              reportName
            });
          } catch (error) {
            console.error('Chunk upload error:', error);
          } finally {
            activeUploads -= 1;
            await startNextChunk();

            const queueEmpty = getUploadQueue(uploadId, resourceType).length === 0;
            if (activeUploads === 0 && queueEmpty) {
              dispatch('setLoadStatus', {
                uploadId,
                resource: resourceType,
                status: LoadStatus.loaded
              });
            }
          }
        })();
        uploadPromises.push(uploadPromise);
      }
      await Promise.all(uploadPromises);
    };

    await startNextChunk();
  },
  async uploadChunk(
    { commit, dispatch },
    { chunk, retry = 0, resource, reportId, reportName, uploadId }
  ) {
    const formData = new FormData();
    let response;

    const reportProgress = (progressEvent) => {
      const progress = progressEvent.total
        ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
        : 0;
      chunk.forEach((file) => {
        const fileKey = `${file.name}_${file.lastModified}`;
        commitProgressIfNeeded(commit, {
          uploadId,
          resourceType: resource,
          fileKey,
          progress,
          fileSize: file.size
        });
      });
    };

    try {
      if (resource === ResourceTypes.JSON) {
        chunk.forEach((file) => formData.append('attrZipFile', file));
        response = await uploadApi.post(
          `/api/examine-report/${reportId}/images/attributes`,
          formData,
          { onUploadProgress: reportProgress }
        );
      } else {
        const tempresource = resource === ResourceTypes.CSV ? 'THERM' : resource;
        chunk.forEach((file) => formData.append('uploadFile', file));
        response = await uploadApi.post(
          `/api/examine-report/${reportId}/upload/${tempresource}`,
          formData,
          { onUploadProgress: reportProgress }
        );
      }

      if (response.data?.result === 'S') {
        chunk.forEach((file) => {
          const fileKey = `${file.name}_${file.lastModified}`;
          commit('SET_UPLOAD_PROGRESS', {
            uploadId,
            resourceType: resource,
            fileKey,
            progress: 100,
            fileSize: file.size
          });
        });
      } else {
        throw new Error(response.data?.resultDesc);
      }
    } catch (error) {
      if (retry < MAX_RETRY) {
        dispatch('setLoadStatus', {
          uploadId,
          resource,
          status: LoadStatus.retry
        });
        return dispatch('uploadChunk', {
          chunk,
          retry: retry + 1,
          resource,
          reportId,
          uploadId,
          reportName
        });
      }
      dispatch('setLoadStatus', {
        uploadId,
        resource,
        status: LoadStatus.error
      });
      chunk.forEach((file) => {
        const fileKey = `${file.name}_${file.lastModified}`;
        commit('ADD_UPLOAD_ERROR', {
          uploadId,
          resourceType: resource,
          fileKey,
          fileSize: file.size,
          error: error?.message || '예기치 못한 오류가 발생하였습니다.'
        });
      });
    }
  },
  resetUpload({ commit }, uploadId) {
    commit('RESET_UPLOAD', { uploadId });
  },
  async getChunkFromQueue({ commit }, { uploadId, resourceType, count }) {
    const queue = getUploadQueue(uploadId, resourceType);
    if (!queue.length) {
      return [];
    }
    const slicedQueue = queue.slice(0, count);
    commit('SLICE_UPLOAD_QUEUE', { uploadId, resourceType, count });
    return slicedQueue;
  },
  async uploadJSONFiles(_ctx, { reportId, files }) {
    const formData = new FormData();
    files.forEach((file) => formData.append('attrZipFile', file));
    await uploadApi.post(`/api/examine-report/${reportId}/images/attributes`, formData);
  },
  setLoadStatus({ commit }, { uploadId, status }) {
    commit('SET_LOAD_STATUS', { uploadId, status });
  },
  resetAllUpload({ commit }) {
    commit('RESET_ALL_UPLOAD');
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
